import { useCallback, useEffect, useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Cog,
  GripVertical,
  Loader2,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import type { JourneyTaskRow as JourneyTask } from "@colab/shared";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAutosave } from "@/hooks/use-autosave";
import { formatApiError } from "@/lib/format-api-error";
import { cn } from "@/lib/utils";
import { useRemoveTask } from "./use-remove-task";
import { useUpdateTask } from "./use-update-task";

// Phase 7 sub-bucket 14 — sortable task row inside the Tasks tab. Three
// editable surfaces:
//
//   1. Inline title — debounced autosave (600ms) via useAutosave → useUpdateTask
//   2. Configure button — opens the task-config-sheet (parent-controlled)
//   3. Remove — destructive AlertDialog confirm
//
// Action kind chip + due offset chip + visibility chip render read-only here;
// editing them happens inside the config sheet.

export function TaskRow({
  journeyId,
  task,
  canDrag,
  actionKindLabel,
  onConfigure,
}: {
  journeyId: string;
  task: JourneyTask;
  canDrag: boolean;
  actionKindLabel: string;
  onConfigure: () => void;
}) {
  const updateTask = useUpdateTask();
  const removeTask = useRemoveTask();
  const [title, setTitle] = useState(task.title);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: task.id, disabled: !canDrag });
  const style = { transform: CSS.Transform.toString(transform), transition };

  useEffect(() => {
    setTitle(task.title);
  }, [task.title]);

  const flushTitle = useCallback(
    async (next: string) => {
      const trimmed = next.trim();
      if (!trimmed || trimmed === task.title) return;
      await updateTask.mutateAsync({
        journeyId,
        taskId: task.id,
        patch: { title: trimmed },
      });
    },
    [journeyId, task.id, task.title, updateTask],
  );

  const titleAutosave = useAutosave<string>({ onFlush: flushTitle, wait: 600 });

  function onTitleChange(next: string) {
    setTitle(next);
    titleAutosave.queue(next);
  }

  function handleDelete() {
    removeTask.mutate(
      { journeyId, taskId: task.id },
      {
        onSuccess: () => {
          toast.success("Task removed");
          setDeleteOpen(false);
        },
        onError: (err) => toast.error(formatApiError(err, "Failed to remove task")),
      },
    );
  }

  const dueLabel =
    task.due_offset_days === 0
      ? "Day 0"
      : `Day +${task.due_offset_days}`;

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-center gap-3 rounded-lg border bg-card px-3 py-2.5 transition-shadow",
        isDragging && "shadow-lg ring-2 ring-accent/40",
      )}
    >
      <button
        type="button"
        aria-label="Drag to reorder"
        className={cn(
          "shrink-0 text-muted-foreground transition-colors",
          canDrag ? "cursor-grab hover:text-foreground active:cursor-grabbing" : "cursor-not-allowed opacity-50",
        )}
        {...attributes}
        {...listeners}
        disabled={!canDrag}
      >
        <GripVertical className="h-4 w-4" />
      </button>

      <Input
        value={title}
        onChange={(e) => onTitleChange(e.target.value)}
        placeholder="Task title"
        maxLength={200}
        aria-label="Task title"
        className="flex-1 border-none bg-transparent px-1 text-sm font-medium focus-visible:ring-0"
      />

      <div className="flex shrink-0 items-center gap-1.5">
        <span className="inline-flex items-center rounded-full border border-border/70 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
          {actionKindLabel}
        </span>
        <span className="inline-flex items-center rounded-full border border-border/70 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground tabular-nums">
          {dueLabel}
        </span>
        {task.visibility === "internal" && (
          <span className="inline-flex items-center rounded-full border border-amber-300/60 bg-amber-50/40 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-amber-800 dark:border-amber-700/40 dark:bg-amber-950/30 dark:text-amber-200">
            Internal
          </span>
        )}
        {titleAutosave.status === "saving" && (
          <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
        )}
      </div>

      <div className="flex shrink-0 items-center gap-1">
        <Button
          type="button"
          size="icon"
          variant="ghost"
          onClick={onConfigure}
          aria-label="Configure task"
          className="h-7 w-7"
        >
          <Cog className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          onClick={() => setDeleteOpen(true)}
          aria-label="Remove task"
          className="h-7 w-7 text-muted-foreground hover:text-destructive"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove task?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the task from the journey. Existing partner
              progress on this task will also be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removeTask.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDelete();
              }}
              disabled={removeTask.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove task
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </li>
  );
}
